import React from 'react'
import Modal from '@jarvis.oddle/core/components/Modal'
import { redirect } from 'src/utils/routes'
import styled from 'styled-components'
import Div from '@jarvis.oddle/core/components/Div'
import Form from '@jarvis.oddle/core/components/Form'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import {
  buttonRadiusValues,
  buttonStyles,
  margin,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import { H2, H4, LinkText, Text } from '@jarvis.oddle/core/components/Typo'
import IconHouse from '@jarvis.oddle/core/components/Icons/IconHouse'
import IconCalendar from '@jarvis.oddle/core/components/Icons/IconCalendar'
import Flex from '@jarvis.oddle/core/components/Flex'
import Divider from '@jarvis.oddle/core/components/Divider'
import MapService, { mapServices } from 'src/containers/MapService'
import {
  checkValidTimeSlot,
  formatDate,
  getTranslatedWeekDays,
  isAfter,
  parseDateString
} from '@jarvis.oddle/core/tools/date'
import { capitalizeFirstLetter, isEmpty, toLowerCase } from 'src/utils/string'
import pickUpDeliveryFlow from 'src/flows/pickUpDeliveryFlow'
import showNotificationToast from '@jarvis.oddle/core/components/ToastNotification'
import useTimer from '@jarvis.oddle/core/hooks/useTimer'
import { isObjectEmpty } from 'src/utils/object'
import { darken, isLight, lighten } from '@jarvis.oddle/core/tools/style'
import ModalContainer from '@jarvis.oddle/core/components/Modal/ModalContainer'
import OneMapApi from '@jarvis.oddle/core/api/map/onemap'
import {
  fetchTimeSlotsAction,
  syncMethodAction,
  syncDeliveryLocationAction,
  syncPickupLocationAction,
  syncAppointmentTimeAction
} from 'src/redux/order/action'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import modalController, {
  makeRouteControlledModal
} from 'src/decorators/ModalController'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectOrder } from 'src/redux/order'
import { useRouter } from 'next/router'

const WrapperForm = styled(Div).attrs(() => ({
  width: '100%',
  as: Form
}))`
  display: contents;
`

// noinspection CssInvalidPropertyValue
const Container = styled(Modal.FlexContainer).attrs(() => ({
  width: '100%',
  height: '100%',
  flexDirection: 'column',
  padding: paddingSizes.medium
}))`
  /* Fix Safari unable to calculate the height */
  /* stylelint-disable value-no-vendor-prefix */
  height: 30%;
  top: 100%;
  transform: translateX(-50%) translateY(-100%);
  min-height: 300px;
  /* stylelint-enable value-no-vendor-prefix */
  background: ${(props) => props.theme.colorContainerColor};
  overflow-y: auto;
  position: relative;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  ${breakpoint('desktop')`
    top: 260px;
    left: 100%;
    width: 100%;
    max-width: 467px;
    height: 100%;
    max-height: 287px;
    box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.2);
    border-radius: 15px;
    margin-left: -245px;
    transform: translateX(-50%) translateY(-50%);
  `}
`

const StyledModalBody = styled(Modal.Body)`
  background-color: transparent;
  align-items: start;
  padding-top: 30px;
`

const StyledModalFooter = styled(Modal.Footer)`
  box-shadow: none;
  justify-content: flex-end;
  padding: 10px 0;
`

const StyledTextDetail = styled(Text).attrs((props) => ({
  color: props.theme.colorBodyFont,
  marginLeft: marginSizes.small
}))``

const StyledLinkText = styled(LinkText)`
  padding: ${padding[paddingSizes.small]};
  margin-right: ${margin[marginSizes.medium]};
  white-space: nowrap;
`

const StyledModal = styled(Modal)`
  z-index: 16000161 !important;
`

const StyledModalContainer = styled(ModalContainer)`
  display: block;
`

const StyledFormSubmitButton = styled(Form.SubmitButton)`
  position: relative;
`

const ButtonText = styled(H4).attrs(() => ({
  normalised: true,
  bold: true
}))`
  color: inherit;
  z-index: 1;
`

const ProgressBar = styled(Div)`
  position: absolute;
  left: 0;
  height: 100%;
  width: ${(props) => props.percent}%;
  background-color: ${(props) =>
    isLight(props.buttonColor)
      ? darken(
          props.secondary || props.buttonStyle === buttonStyles.outline
            ? 'transparent'
            : props.theme.buttonColor,
          15
        )
      : lighten(
          props.secondary || props.buttonStyle === buttonStyles.outline
            ? 'transparent'
            : props.theme.buttonColor,
          15
        )};
  ${(props) =>
    props.percent < 90 && props.percent > 10 ? 'transition: width 2s' : ''};
  opacity: 0.4;
  border-top-left-radius: ${(props) =>
    buttonRadiusValues[props.theme.buttonShape]};
  border-bottom-left-radius: ${(props) =>
    buttonRadiusValues[props.theme.buttonShape]};
  border-top-right-radius: ${(props) =>
    props.percent < 100 ? '0' : buttonRadiusValues[props.theme.buttonShape]};
  border-bottom-right-radius: ${(props) =>
    props.percent < 100 ? '0' : buttonRadiusValues[props.theme.buttonShape]};
  background-color: ${(props) =>
    !isLight(props.buttonColor)
      ? darken(
          props.secondary || props.buttonStyle === buttonStyles.outline
            ? 'transparent'
            : props.theme.buttonColor,
          15
        )
      : lighten(
          props.secondary || props.buttonStyle === buttonStyles.outline
            ? 'transparent'
            : props.theme.buttonColor,
          15
        )};
`

const EatsFilterHandlerModal = ({
  modal,
  mapApi
}: {
  modal: {
    show: boolean
    onClose: (handler?) => void
    onClosed: () => void
  }
  mapApi: {
    getPlaceDetails: (address) => Promise<{
      formattedAddress: string
      country: string
      addressOne: string
      postal: string
      latitude: string
      longitude: string
      city: string
      state: string
    }>
  }
}) => {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const { t } = React.useContext(TranslationContext)
  const [header, setHeader] = React.useState('')
  const [dateTimeSlot, setDateTimeSlot] = React.useState('')
  const [isValidDateTime, setIsValidDateTime] = React.useState(null)
  const [placeInfo, setPlaceInfo] = React.useState(
    {} as {
      formattedAddress: string
      country: string
      addressOne: string
      postal: string
      latitude: string
      longitude: string
      city: string
      state: string
    }
  )
  const orderState = useAppSelector(selectOrder)
  const storeOrder = orderState.storeOrder
  const durationInMs = 10000
  const { timeLeft, start, stop } = useTimer(durationInMs)
  const {
    address,
    collectionDate,
    collectionMethod = '',
    collectionTimeSlot,
    locale
  } = router.query as Record<string, string | undefined>
  const [availableTimeSlot, setAvailableTimeSlot] = React.useState('')
  const [percentCountdown, setPercentCountdown] = React.useState(100)
  const [availableDate, setAvailableDate] = React.useState('')
  const earliestCollectionTime = storeOrder.data.earliestCollectionTime

  // handle to verify date, timeslot and placeId
  React.useEffect(() => {
    async function fetchFilterInfo() {
      let parsedCollectionDate = new Date(collectionDate)
      // check collectionDate from Eats is valid or not
      if (isAfter(new Date(), parsedCollectionDate)) {
        const parsedEarliestCollectionTime = parseDateString(
          earliestCollectionTime,
          'DD MM YYYY'
        )
        parsedCollectionDate = new Date(parsedEarliestCollectionTime)
        setAvailableDate(parsedEarliestCollectionTime)
      } else {
        setAvailableDate(collectionDate)
      }
      // checking date and timeslot is valid or not
      const timeSlots = await dispatch(
        fetchTimeSlotsAction({
          date: parsedCollectionDate
        })
      )
      if (
        timeSlots.meta.requestStatus === 'fulfilled' &&
        timeSlots.payload.payload.timeIntervals
      ) {
        const { isValidTimeSlot, availableTimeSlot } = checkValidTimeSlot({
          date: parsedCollectionDate,
          currentTimeSlot: collectionTimeSlot,
          availableTimeSlots: timeSlots.payload.payload.timeIntervals
        })

        if (collectionMethod === 'pickup' && isValidTimeSlot) {
          setHeader(
            t("We've selected the nearest store and pickup date for you")
          )
        } else if (collectionMethod === 'pickup' && !isValidTimeSlot) {
          setHeader(t('Your preferred pickup date is no longer available'))
        } else if (collectionMethod === 'delivery' && isValidTimeSlot) {
          setHeader(t("We've set the delivery address and date for you"))
        } else if (collectionMethod === 'delivery' && !isValidTimeSlot) {
          setHeader(t('Your preferred delivery date is no longer available'))
        }
        setIsValidDateTime(isValidTimeSlot)
        setAvailableTimeSlot(availableTimeSlot)
        setDateTimeSlot(
          `${
            getTranslatedWeekDays(t, 'long')[parsedCollectionDate.getDay()]
          }, ${formatDate({
            t,
            date: parsedCollectionDate,
            locale,
            options: {
              weekday: undefined,
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            }
          })}, ${availableTimeSlot}`
        )
      }

      // checking placeId is valid or not
      const placeDetails = await mapApi.getPlaceDetails(address)
      if (!(placeDetails || {}).addressOne) {
        OneMapApi()
          .getApiHealthStatus()
          .then((response) => {
            if (response) {
              return OneMapApi()
                .findPlaceFromQuery(placeDetails.postal)
                .then((place) => {
                  if (
                    place.length > 0 &&
                    place[0].formattedAddress &&
                    place[0].addressOne
                  ) {
                    setPlaceInfo(place[0])
                    start()
                  }
                })
            }
            return null
          })
      } else if ((placeDetails || {}).formattedAddress) {
        setPlaceInfo(placeDetails)
        start()
      }
    }

    if (
      address &&
      collectionDate &&
      collectionMethod &&
      collectionTimeSlot &&
      mapApi.getPlaceDetails &&
      earliestCollectionTime
    ) {
      fetchFilterInfo()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    address,
    collectionDate,
    collectionMethod,
    collectionTimeSlot,
    mapApi.getPlaceDetails,
    earliestCollectionTime
  ])

  const handleUpdateAddress = React.useCallback(async () => {
    await dispatch(syncMethodAction({ method: collectionMethod }))

    if ((placeInfo || {}).formattedAddress) {
      if (collectionMethod === 'delivery') {
        const deliveryAddress = await dispatch(
          syncDeliveryLocationAction({
            address: {
              deliveryAddress: placeInfo.formattedAddress,
              deliveryAddressCountry: placeInfo.country,
              deliveryAddressOne: placeInfo.addressOne,
              deliveryAddressPostal: placeInfo.postal,
              deliveryAddressTwo: '',
              latitude: placeInfo.latitude,
              longitude: placeInfo.longitude,
              deliveryAddressCity: placeInfo.city,
              deliveryAddressState: placeInfo.state
            }
          })
        )
        return (
          deliveryAddress.meta.requestStatus === 'fulfilled' &&
          Boolean(deliveryAddress.payload.payload?.storeOrder)
        )
      }
      const nearestStore = await dispatch(
        syncPickupLocationAction({
          storeId: null,
          lat: placeInfo.latitude,
          long: placeInfo.longitude,
          selectedDate: availableDate
        })
      )
      return (
        nearestStore.meta.requestStatus === 'fulfilled' &&
        Boolean(nearestStore.payload.payload?.storeOrder)
      )
    }
    return false
  }, [availableDate, collectionMethod, dispatch, placeInfo])

  const handleSubmitForm = React.useCallback(
    async (showToast = false) => {
      const isValidAddress = await handleUpdateAddress()
      const timeSlots = await dispatch(
        fetchTimeSlotsAction({
          date: availableDate
        })
      )
      if (!isValidDateTime) {
        pickUpDeliveryFlow.start({
          ...(isValidAddress ? { type: 'dateTimeSelection' } : {}),
          onFinished: () => modalController.showModal('CartDrawer')
        })
        return
      }
      const validTimeSlot = (
        timeSlots.payload?.payload?.timeIntervals || []
      ).find(
        (timeSlot) =>
          toLowerCase(timeSlot.y.split(' to ')[0]) ===
          toLowerCase(availableTimeSlot.split(' to ')[0])
      )
      if (validTimeSlot) {
        await dispatch(
          syncAppointmentTimeAction({
            collectionDate: availableDate,
            collectionTimeSlot: availableTimeSlot,
            collectionTimeSlotId: validTimeSlot.x
          })
        )
        pickUpDeliveryFlow.updateMeta({ isVisited: true })
        if (showToast) showNotificationToast.success(t('Successfully updated'))
      } else if (showToast) {
        showNotificationToast.error(
          t("We're sorry, but something went wrong on our end.")
        )
      }
    },
    [
      handleUpdateAddress,
      isValidDateTime,
      availableTimeSlot,
      dispatch,
      availableDate,
      t
    ]
  )

  // auto close modal after 5 seconds
  React.useEffect(() => {
    if (isValidDateTime && !isObjectEmpty(placeInfo) && modal.show) {
      if (timeLeft === 0) {
        setPercentCountdown(100)
        stop()
        modal.onClose(handleSubmitForm)
      } else {
        setPercentCountdown(100 - (timeLeft * 100) / durationInMs)
      }
    }
  }, [handleSubmitForm, isValidDateTime, modal, placeInfo, stop, timeLeft])

  const handleClickChange = () => {
    if (!isValidDateTime) {
      modal.onClose()
    } else {
      modal.onClose(() =>
        pickUpDeliveryFlow.start({
          onFinished: () => modalController.showModal('CartDrawer')
        })
      )
    }
  }

  const onFormSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true)
    modal.onClose(() => handleSubmitForm(true))
    setSubmitting(false)
  }

  return (
    <StyledModal
      {...modal}
      show={
        modal.show &&
        !isEmpty(header) &&
        isValidDateTime !== null &&
        !isObjectEmpty(placeInfo)
      }
      slideInAnimation='right'
      withoutBackdrop
      withoutFocusTrap
      Container={StyledModalContainer}
    >
      <WrapperForm onSubmit={onFormSubmit}>
        <Container>
          <Modal.Header boxShadow='none' justifyContent='flex-start'>
            <H2 bold normalised>
              {header}
            </H2>
          </Modal.Header>
          <StyledModalBody>
            {isValidDateTime ? (
              <>
                <Flex>
                  <IconHouse />
                  <StyledTextDetail>
                    {placeInfo.formattedAddress}
                  </StyledTextDetail>
                </Flex>
                <Divider />
                <Flex>
                  <IconCalendar />
                  <StyledTextDetail>{dateTimeSlot}</StyledTextDetail>
                </Flex>
              </>
            ) : (
              <Flex>
                {t(
                  '_collectionMethod_ slots for _dateTimeSlot_ are no longer available. Would you like to select another delivery date?',
                  {
                    collectionMethod: capitalizeFirstLetter(collectionMethod),
                    dateTimeSlot
                  }
                )}
              </Flex>
            )}
          </StyledModalBody>
          <StyledModalFooter>
            <StyledLinkText heading onClick={handleClickChange}>
              {isValidDateTime ? t('Change') : t('Skip')}
            </StyledLinkText>
            <StyledFormSubmitButton>
              <ProgressBar percent={isValidDateTime ? percentCountdown : 0} />
              <ButtonText>
                {isValidDateTime
                  ? `${t('Continue')} (${timeLeft / 1000})`
                  : t('Select a _appointmentType_ Date', {
                      appointmentType: capitalizeFirstLetter(collectionMethod)
                    })}
              </ButtonText>
            </StyledFormSubmitButton>
          </StyledModalFooter>
        </Container>
      </WrapperForm>
    </StyledModal>
  )
}

const WrapperComponent = (props) => (
  <MapService mapService={mapServices.GOOGLE_MAP}>
    {(mapServiceProps) => (
      <EatsFilterHandlerModal {...mapServiceProps} {...props} />
    )}
  </MapService>
)

export default makeRouteControlledModal(
  WrapperComponent,
  'EatsFilterHandlerModal',
  ({
    query: { address, collectionDate, collectionMethod, collectionTimeSlot }
  }) => address && collectionDate && collectionMethod && collectionTimeSlot,
  (router) =>
    redirect({
      url: '/[locale]',
      as: `/${router.query.locale}?referrer=${router.query.referrer}&utm_source=${router.query.utm_source}&utm_medium=${router.query.utm_medium}`,
      scroll: false,
      shallow: true
    })
)
